/**
 * What the rest of the platform is doing to the stands.
 *
 * This is the join that makes the flight layer part of the airport rather
 * than a timetable beside it. A pavement defect raised as a work order
 * against an apron takes the stands on that apron out of the allocation, and
 * nobody has to tell the flight layer separately — it reads the same work
 * orders everything else reads.
 *
 * Two rules keep it honest:
 *
 *  · **Only open work stops a stand.** A completed or verified order is a
 *    repair that happened, not a closure; counting it would shut the airport
 *    down by history.
 *  · **The match is named.** A stand is blocked either because the order
 *    names the stand's own zone, or because its title names the stand. Both
 *    are stated in the reason, so somebody who disagrees can see which rule
 *    caught it rather than arguing with a black box.
 *
 * It takes plain records rather than the work-order or closure types, so the
 * stands domain depends on neither and this join can be tested on its own.
 */
import type { StandBlock, Stand } from "../../domain/stands";
import { isOpen, type WorkOrder } from "../../domain/workOrders";
import { t } from "../../i18n";

/** A closure of a surface, as the closures register holds it. */
export interface SurfaceClosure {
  ref: string;
  zoneCode?: string;
  reason?: string;
  from?: string;
  to?: string;
}

/**
 * Work orders that close a stand.
 *
 * A stand is caught when the order's zone is the stand's zone, or when the
 * order's title or asset names the stand. The second matters because an
 * airport writes "Stand 85" in a title far more often than it fills in a
 * zone code.
 */
export function blocksFromWorkOrders(stands: readonly Stand[], orders: readonly WorkOrder[]): StandBlock[] {
  const blocks: StandBlock[] = [];
  for (const wo of orders) {
    if (!isOpen(wo.status)) continue;
    for (const stand of stands) {
      const byZone = Boolean(stand.zoneCode && wo.zoneCode && stand.zoneCode === wo.zoneCode);
      const named = new RegExp(`\\b(stand\\s*)?${stand.code}\\b`, "i");
      const byName = named.test(wo.title) || (wo.assetTag ? named.test(wo.assetTag) : false);
      if (!byZone && !byName) continue;
      blocks.push({
        standCode: stand.code,
        kind: "work order",
        ref: wo.number,
        /*
         * Naming the stand closes it. Sharing an apron with it does not: a
         * marking repaint on one stand is not a closure of the apron, and
         * treating it as one shut thirteen of fifteen stands here on a day
         * with two jobs open. Surfaces are closed by CLOSURES.
         */
        severity: byName ? "closed" : "caution",
        why: byName
          ? t("{title} — the order names this stand", { title: wo.title })
          : t("{title} — open work on {zone}", { title: wo.title, zone: stand.zoneCode ?? "" }),
      });
    }
  }
  return blocks;
}

/** Closures of a surface the stand sits on. */
export function blocksFromClosures(stands: readonly Stand[], closures: readonly SurfaceClosure[]): StandBlock[] {
  const blocks: StandBlock[] = [];
  for (const c of closures) {
    if (!c.zoneCode) continue;
    for (const stand of stands) {
      if (stand.zoneCode !== c.zoneCode) continue;
      blocks.push({
        standCode: stand.code,
        kind: "closure",
        ref: c.ref,
        from: c.from,
        to: c.to,
        /* a closure of a surface does close the stands on it */
        severity: "closed",
        why: c.reason ?? t("the surface it stands on is closed"),
      });
    }
  }
  return blocks;
}
